/*
 * Copyright (c)  CYBERKOALA LLC 2025.
 * Proprietary License.
 * Redistribution and use in source and binary forms, with or without
 * modification, are restricted.
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { API_BASE } from '@/config.ts';

export interface GameApplication {
  process: string;
  name?: string | null;
  steamAppId?: number | null;
  iconUrl?: string | null;
}

export interface GameEntry {
  userId: string;
  process: string;
  startedAt: string;
  endedAt: string | null;
  updatedAt: string;
  duration: number | null;
  application: GameApplication | null;
}

/**
 * @example
 * const { fetchServerGames, getServerGames, loading } = useServerGamesStore();
 *
 * useEffect(() => {
 *   fetchServerGames(server.id);
 * }, [server.id, fetchServerGames]);
 *
 * const games = getServerGames(server.id) ?? [];
 * const isLoading = loading[server.id];
 */
interface ServerGamesState {
  gamesByServer: Record<string, GameEntry[]>;
  loading: Record<string, boolean>;
  errors: Record<string, string | null>;
  eventSources: Record<string, EventSource | null>;

  fetchServerGames: (serverId: string) => Promise<void>;
  setServerGames: (serverId: string, data: GameEntry[]) => void;
  getServerGames: (serverId: string) => GameEntry[] | undefined;
  connectToServerStream: (serverId: string) => void;
  disconnectFromServerStream: (serverId: string) => void;
  reset: (serverId?: string) => void;
}

export const useServerGamesStore = create<ServerGamesState>()(
  devtools((set, get) => ({
    gamesByServer: {},
    loading: {},
    errors: {},
    eventSources: {},

    async fetchServerGames(serverId) {
      const { loading } = get();
      if (loading[serverId]) return; // prevent double fetches

      set((state) => ({
        loading: { ...state.loading, [serverId]: true },
        errors: { ...state.errors, [serverId]: null },
      }));

      try {
        const res = await fetch(`${API_BASE}/api/activity/games/server/${serverId}`, {
          credentials: 'include',
        });
        if (!res.ok) throw new Error(await res.text());
        const json = await res.json();

        set((state) => ({
          gamesByServer: {
            ...state.gamesByServer,
            [serverId]: json.games || [],
          },
          loading: { ...state.loading, [serverId]: false },
        }));
      } catch (err: any) {
        console.error(`Failed to fetch server games for ${serverId}:`, err);
        set((state) => ({
          errors: {
            ...state.errors,
            [serverId]: err?.message || 'Unknown error',
          },
          loading: { ...state.loading, [serverId]: false },
        }));
      }
    },

    setServerGames(serverId, data) {
      set((state) => ({
        gamesByServer: { ...state.gamesByServer, [serverId]: data },
      }));
    },

    getServerGames(serverId) {
      return get().gamesByServer[serverId];
    },

    /**
     *  DONT USE, causes error on reconnects
     *  @experimental
     * node:_http_outgoing:642
     *     throw new ERR_HTTP_HEADERS_SENT('set');
     *     ^
     *
     * Error [ERR_HTTP_HEADERS_SENT]: Cannot set headers after they are sent to the client
     * @param serverId
     */
    connectToServerStream: (serverId: string) => {
      const { eventSources } = get(); // Prevent duplicate connections
      if (eventSources[serverId]) return;

      const source = new EventSource(
        `${API_BASE}/api/activity/games/stream/${serverId}`,
        {
          withCredentials: true,
        }
      );

      set((state) => ({
        loading: { ...state.loading, [serverId]: true },
        errors: { ...state.errors, [serverId]: null },
        eventSources: { ...state.eventSources, [serverId]: source },
      }));

      source.onmessage = (event) => {
        const data = JSON.parse(event.data);
        if (data.games) {
          set((state) => ({
            gamesByServer: {
              ...state.gamesByServer,
              [serverId]: data.games,
            },
            loading: { ...state.loading, [serverId]: false },
          }));
        }
      };

      source.onerror = (err) => {
        console.error(`[SSE] Server ${serverId} error:`, err);
        set((state) => ({
          errors: { ...state.errors, [serverId]: 'Connection lost' },
          loading: { ...state.loading, [serverId]: false },
        }));
      };
    },

    disconnectFromServerStream: (serverId: string) => {
      const { eventSources } = get();
      const source = eventSources[serverId];
      if (source) {
        source.close();
        set((state) => {
          const updated = { ...state.eventSources };
          delete updated[serverId];
          return { eventSources: updated };
        });
      }
    },

    reset(serverId) {
      if (!serverId) {
        set({ gamesByServer: {}, loading: {}, errors: {} });
      } else {
        set((state) => {
          const newGames = { ...state.gamesByServer };
          const newLoading = { ...state.loading };
          const newErrors = { ...state.errors };
          delete newGames[serverId];
          delete newLoading[serverId];
          delete newErrors[serverId];
          return {
            gamesByServer: newGames,
            loading: newLoading,
            errors: newErrors,
          };
        });
      }
    },
  }))
);